import type { GuardrailOverrideToken, GuardrailViolation } from './types';
import { isRecord, OVERRIDE_HEADER, parseAttemptId } from './types';

const REASON_SEPARATOR = '; ';

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

/**
 * Merge the current round's violation keys into the accumulated set, preserving prior order
 * and de-duplicating (each round's list is added to prior rounds so warnings acknowledged
 * earlier aren't dropped from the token — DR §6.8).
 */
export const accumulateKeys = (prev: string[], violations: GuardrailViolation[]): string[] => [
  ...new Set([...prev, ...violations.map((v) => v.key)]),
];

/**
 * Combine override reasons across replay rounds into the single reason the backend stores
 * for the whole action (DR §6.8 names "merge" as the target). Non-blank reasons are joined.
 * Dedup is an exact whole-string check only: because the accumulator is a flat
 * separator-joined string, splitting it to compare per segment is unsafe when a user-typed
 * reason itself contains the separator (it would drop a distinct reason that matches a
 * fragment). An occasional duplicated reason in the free-text note is acceptable; silently
 * losing one is not.
 */
export const mergeReasons = (prev: string, next: string): string => {
  const trimmed = next.trim();
  if (!trimmed) {
    return prev;
  }
  if (!prev || prev === trimmed) {
    return prev || trimmed;
  }
  return `${prev}${REASON_SEPARATOR}${trimmed}`;
};

/**
 * Serialize the override token for the `X-Guardrail-Override` header. Non-ASCII code units
 * are escaped to `\uXXXX` so the value is pure ASCII: HTTP header values are coerced to
 * ByteString and XHR's `setRequestHeader` throws on any code point > U+00FF — a free-text
 * `override_reason` with Hebrew/other scripts, an emoji, or a pasted curly quote / em-dash
 * would otherwise throw synchronously and abort the replay. The escaped form is still valid
 * JSON that the backend's `json.loads` decodes identically, so this needs no contract change.
 */
export const buildOverrideHeaderValue = (token: GuardrailOverrideToken): string => {
  const json = JSON.stringify(token);
  let out = '';
  for (let i = 0; i < json.length; i += 1) {
    const code = json.charCodeAt(i);
    // Escape every code unit above ASCII (surrogate pairs escape per-unit, which JSON.parse
    // recombines) so the header carries no byte a ByteString coercion would reject.
    out += code > 127 ? `\\u${code.toString(16).padStart(4, '0')}` : json[i];
  }
  return out;
};

/** The fields carried forward from a prior round's override token. */
export type CarriedOverrideState = Pick<GuardrailOverrideToken, 'override_reason' | 'acknowledged_keys' | 'attempt_id'>;

/**
 * Parse a prior `X-Guardrail-Override` header value into the state carried across replay
 * rounds, or `null` if absent/malformed. Used to accumulate `acknowledged_keys` + merge
 * `override_reason` via the request itself (no external state): each round reads the previous
 * round's token off the failed request's config headers and extends it. `tripped_at` is
 * intentionally not surfaced — each round stamps a fresh one from its own 409 (DR §6.8), so
 * the prior value is unused.
 */
export const parseOverrideHeaderValue = (raw: unknown): CarriedOverrideState | null => {
  if (typeof raw !== 'string' || !raw) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isRecord(parsed) && typeof parsed.override_reason === 'string' && isStringArray(parsed.acknowledged_keys)) {
      return {
        override_reason: parsed.override_reason,
        acknowledged_keys: parsed.acknowledged_keys,
        attempt_id: parseAttemptId(parsed.attempt_id),
      };
    }
  } catch {
    // fall through to null
  }
  return null;
};

/**
 * Find the override header on a request-config headers object, case-insensitively. HTTP
 * header names are case-insensitive and axios may normalize them (e.g. to
 * `x-guardrail-override`), so a direct keyed read would miss a normalized header and drop the
 * accumulated override state across replay rounds. Returns the actual key (so the caller can
 * delete it before re-setting a canonically-cased one) and its value.
 */
export const findOverrideHeader = (headers: unknown): { key: string; value: unknown } | null => {
  if (!isRecord(headers)) {
    return null;
  }
  const key = Object.keys(headers).find((k) => k.toLowerCase() === OVERRIDE_HEADER.toLowerCase());
  return key === undefined ? null : { key, value: headers[key] };
};
